import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
  Animated,
  RefreshControl,
  LayoutAnimation,
  UIManager,
  ScrollView,
  Modal,
  Pressable,
  Dimensions,
} from 'react-native';
import {
  useNavigation,
  CommonActions,
  useFocusEffect,
  useRoute,
} from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptionUsage } from '../../hooks/useSubscription';
import { RootStackParamList } from '../../types/navigation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import UserAvatar from '../../components/shared/UserAvatar';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';
import { LinearGradient } from 'expo-linear-gradient';
import { navigate } from '../../navigation/NavigationService';
import { getMobileTabForRoute } from '../../utils/mobileTabNavigation';
import { Button } from '../../components';
import { palette } from '../../utils/palette';
import { getColourProfiles } from '../../api/colours';
import { getToken } from '../../utils/storage';
import { getSeasonDescription } from '../../constants/seasons';
import { blogArticles, getFeaturedArticles } from '../../data/blogArticles';
import Badge, { BadgeData } from '../../components/shared/Badge';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { WelcomeModal, TourTooltipContent } from '../../components/shared';
import { useTour } from '../../context/TourContext';
import {
  getHasCompletedTour,
  markTourCompleted,
} from '../../utils/tourPersistence';
import {
  legacyUndertoneForApi,
  parseUndertoneFromStorage,
  type SeasonUndertone,
} from '../../utils/seasonUndertone';

const SHOW_COLOR_ANALYSIS_WIZARD_KEY = 'SHOW_COLOR_ANALYSIS_WIZARD';
const USE_MODERN_HOME = true;
const MOBILE_TAB_BAR_HEIGHT = 65;
const MOBILE_TAB_BAR_EXTRA = 10;

type SpotlightRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Positions tour tooltip; on mobile places below spotlight when placement is bottom. */
function getTourJourneyTooltipWrapStyle(
  placement: 'top' | 'bottom' | 'left' | 'right',
  spotlightRect: SpotlightRect,
  windowHeight: number,
  windowWidth: number,
  isMobile: boolean,
  bottomInset: number
) {
  const gap = 16;
  const tabBarClearance =
    MOBILE_TAB_BAR_HEIGHT + bottomInset + MOBILE_TAB_BAR_EXTRA + gap;

  if (isMobile) {
    if (placement === 'bottom') {
      const top = spotlightRect.y + spotlightRect.height + gap;
      return {
        left: 16,
        right: 16,
        top,
        maxHeight: Math.max(120, windowHeight - top - tabBarClearance),
      };
    }
    return {
      left: 16,
      right: 16,
      bottom: tabBarClearance,
    };
  }
  if (placement === 'right') {
    return {
      left: spotlightRect.x + spotlightRect.width + gap,
      right: gap,
      top: spotlightRect.y,
      maxHeight: spotlightRect.height + 120,
    };
  }
  if (placement === 'left') {
    return {
      right: windowWidth - spotlightRect.x + gap,
      left: gap,
      top: spotlightRect.y,
      maxHeight: spotlightRect.height + 120,
    };
  }
  if (placement === 'top') {
    return {
      left: 16,
      right: 16,
      bottom: windowHeight - spotlightRect.y + gap,
    };
  }
  return {
    left: 16,
    right: 16,
    top: spotlightRect.y + spotlightRect.height + gap,
  };
}
import {
  trackButtonClick,
  trackAnalyticsEvent,
  trackTutorialEvent,
} from '../../utils/analytics';
import ModernHomeContent, {
  type TourHighlightTarget,
  type TourHighlightRect,
} from './ModernHomeContent';

type HomeScreenNavigationProp = NativeStackNavigationProp<RootStackParamList>;

// Enhanced user journey data with dynamic functionality
interface UserProfileInput {
  hasCompletedAnalysis?: boolean;
  hasCreatedPalette?: boolean;
  hasUsedVirtualDrape?: boolean;
  hasOrganizedWardrobe?: boolean;
  undertone?: SeasonUndertone | string;
  season?: string;
  recommendedColors?: string[];
}
const getInitialUserJourneyData = (userProfile?: UserProfileInput) => {
  const hasCompletedAnalysis = userProfile?.hasCompletedAnalysis || false;
  const hasCreatedPalette = userProfile?.hasCreatedPalette || false;
  const hasUsedVirtualDrape = userProfile?.hasUsedVirtualDrape || false;
  const hasOrganizedWardrobe = userProfile?.hasOrganizedWardrobe || false;

  let currentStep = 1;
  let progress = 0;

  if (hasCompletedAnalysis) {
    currentStep = 2;
    progress = 25;
  }
  if (hasCreatedPalette) {
    currentStep = 2;
    progress = 50;
  }
  if (hasUsedVirtualDrape) {
    currentStep = 3;
    progress = 75;
  }
  if (hasOrganizedWardrobe) {
    currentStep = 4;
    progress = 100;
  }

  const nextMilestones = [
    'Complete your first colour analysis',
    'Create your first colour palette',
    'Complete your first virtual drape test',
    'Organise your first wardrobe items',
  ];

  return {
    currentStep,
    totalSteps: 4,
    progress,
    nextMilestone:
      nextMilestones[currentStep - 1] || 'Complete your colour journey',
    achievements: [
      {
        id: 1,
        title: 'Colour Analysis Complete',
        icon: 'checkmark-circle',
        earned: hasCompletedAnalysis,
      },
      {
        id: 2,
        title: 'First Palette Created',
        icon: 'color-palette',
        earned: hasCreatedPalette,
      },
      {
        id: 3,
        title: 'Virtual Drape Master',
        icon: 'shirt',
        earned: hasUsedVirtualDrape,
      },
      {
        id: 4,
        title: 'Wardrobe Organizer',
        icon: 'library',
        earned: hasOrganizedWardrobe,
      },
    ],
    dailyInsight: getDailyColourInsight(userProfile?.undertone || 'spring'),
  };
};

// Dynamic daily colour insights based on user's undertone
const getDailyColourInsight = (undertone: string) => {
  const undertoneGroup = legacyUndertoneForApi(
    parseUndertoneFromStorage(undertone)
  );
  const insights = {
    warm: {
      title: "Today's Colour Wisdom",
      message:
        'Warm undertones shine in golden yellows and rich oranges. Try incorporating these hues into your outfit today!',
      color: '#FFB703',
      category: 'Warm Tones',
    },
    cool: {
      title: "Today's Colour Wisdom",
      message:
        'Cool undertones look stunning in blues and purples. Consider adding these calming colours to your look!',
      color: '#4ECDC4',
      category: 'Cool Tones',
    },
    neutral: {
      title: "Today's Colour Wisdom",
      message:
        'Neutral undertones can pull off almost any colour! Try experimenting with bold, vibrant hues today.',
      color: '#A78BFA',
      category: 'Neutral Tones',
    },
  };

  return insights[undertoneGroup] || insights.warm;
};

// Dynamic personalized recommendations based on user profile
const getPersonalizedRecommendations = (userProfile?: UserProfileInput) => {
  const undertone = legacyUndertoneForApi(
    parseUndertoneFromStorage(userProfile?.undertone)
  );
  const season = userProfile?.season || 'Spring';

  const colourPalettes = {
    warm: {
      spring: {
        name: 'Light Spring',
        colors: ['#F0D878', '#F5DEB3', '#FFDAB9', '#FEBAAD', '#81D8D0'],
        description:
          "Perfect for today's sunny weather and your warm undertones",
      },
      summer: {
        name: 'True Spring',
        colors: ['#FFD700', '#FFA500', '#FF7F50', '#FF6347', '#F5A25D'],
        description: 'Embrace the summer warmth with these vibrant hues',
      },
      autumn: {
        name: 'Warm Spring',
        colors: ['#FFE033', '#FF4500', '#FC6C85', '#00BFFF', '#4169E1'],
        description: 'Rich autumn colors that complement your warm undertones',
      },
      winter: {
        name: 'Warm Spring Bold',
        colors: ['#7CFC00', '#FF0000', '#F8CFA9', '#FF69B4', '#7FFFD4'],
        description: 'Bold winter colors that warm up the season',
      },
    },
    cool: {
      spring: {
        name: 'Light Summer',
        colors: ['#F7CAC9', '#ADD8E6', '#A8D8D8', '#E0BBE4', '#C3CDE6'],
        description: 'Fresh spring colors that complement your cool undertones',
      },
      summer: {
        name: 'True Summer',
        colors: ['#4682B4', '#5F9EA0', '#4C516D', '#C08081', '#9F9BC2'],
        description: 'Cool summer tones that keep you refreshed',
      },
      autumn: {
        name: 'Cool Summer',
        colors: ['#BC8F8F', '#8F9779', '#6A7985', '#847996', '#8D646A'],
        description: 'Sophisticated autumn colors for cool undertones',
      },
      winter: {
        name: 'Cool Summer Deep',
        colors: ['#708090', '#987D80', '#394146', '#D0BABA', '#7B9095'],
        description: 'Crisp winter colors that make you shine',
      },
    },
    neutral: {
      spring: {
        name: 'Soft Autumn',
        colors: ['#8A9A5B', '#D1856A', '#987456', '#BBA14A', '#678C8A'],
        description: 'Versatile spring colors that work with any undertone',
      },
      summer: {
        name: 'True Neutral',
        colors: ['#DC143C', '#008080', '#9370DB', '#CD5C5C', '#F0E68C'],
        description: 'Soft summer colors that adapt to your natural tones',
      },
      autumn: {
        name: 'Deep Neutral',
        colors: ['#000000', '#3D0C02', '#0A4F4F', '#013220', '#000080'],
        description: 'Neutral autumn colors that enhance your natural beauty',
      },
      winter: {
        name: 'Deep Neutral Rich',
        colors: ['#800020', '#4B0082', '#556B2F', '#483C32', '#8B7500'],
        description: 'Classic winter colors that work with any undertone',
      },
    },
  };

  const palette =
    colourPalettes[undertone as keyof typeof colourPalettes]?.[
      season.toLowerCase() as keyof typeof colourPalettes.warm
    ] || colourPalettes.warm.spring;

  const moods = [
    'Energetic',
    'Calm',
    'Confident',
    'Creative',
    'Professional',
    'Playful',
  ];
  const todayMood = moods[Math.floor(Math.random() * moods.length)];

  return {
    season,
    undertone,
    dominantColors: palette.colors,
    todayMood,
    recommendedPalette: palette,
  };
};

const FEATURE_CARDS = [
  {
    id: 'color-analysis',
    title: 'Discover Your Colours',
    subtitle: 'AI-Powered Analysis',
    description:
      'Unlock your unique colour DNA with our advanced skin tone analysis',
    icon: 'color-palette',
    gradient: ['#FF6B9D', '#FF8E53'],
    bgGradient: ['#FFF5F7', '#FFF8F0'],
    route: 'ColourAnalysis',
    features: [
      'Personal colour palette',
      'Skin tone analysis',
      'Seasonal recommendations',
    ],
    accentColor: '#FF6B9D',
    image:
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&crop=center',
    story:
      'Every person has a unique colour signature. Discover yours in just 2 minutes.',
    difficulty: 'Beginner',
    timeEstimate: '2 min',
    popularity: 98,
  },
  {
    id: 'virtual-drape',
    title: 'Try Before You Buy',
    subtitle: 'Virtual Colour Testing',
    description: 'See how colours look on you instantly with our AR technology',
    icon: 'shirt',
    gradient: ['#4ECDC4', '#44A08D'],
    bgGradient: ['#F0FFFD', '#F0F9FF'],
    route: 'VirtualDrapeTest',
    features: ['Real-time testing', 'Camera integration', 'Instant feedback'],
    accentColor: '#4ECDC4',
    image:
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop&crop=center',
    story:
      'No more guessing games. Test any colour against your skin tone instantly.',
    difficulty: 'Easy',
    timeEstimate: '1 min',
    popularity: 95,
  },
  {
    id: 'color-generator',
    title: 'Colour Generator',
    subtitle: 'Create Palettes',
    description: 'Generate harmonious colour palettes and save your favourites',
    icon: 'sparkles',
    gradient: ['#FFB703', '#F7931E'],
    bgGradient: ['#FFFBF0', '#FFF8E7'],
    route: 'ColorGenerator',
    features: ['Palette generator', 'Export & save', 'Harmony rules'],
    accentColor: '#E5A00D',
    image: '',
    story: 'Build custom palettes in seconds with smart colour harmony.',
    difficulty: 'Easy',
    timeEstimate: '1 min',
    popularity: 90,
  },
  {
    id: 'color-library',
    title: 'Colour Library',
    subtitle: 'Browse & Explore',
    description: 'Explore colours, copy hex codes, and find inspiration',
    icon: 'library',
    gradient: ['#8B5CF6', '#6366F1'],
    bgGradient: ['#F5F3FF', '#EEF2FF'],
    route: 'ColorLibrary',
    features: ['Browse colours', 'Copy hex codes', 'Search & filter'],
    accentColor: '#8B5CF6',
    image: '',
    story: 'Your reference for every shade. Search, copy, and organise.',
    difficulty: 'Beginner',
    timeEstimate: '1 min',
    popularity: 88,
  },
];

interface HomeScreenProps {
  onContentChange?: (content: string) => void;
  onTabPress?: (tab: string) => void;
}

const HomeScreen: React.FC<HomeScreenProps> = ({
  onContentChange,
  onTabPress,
}) => {
  const navigation = useNavigation<HomeScreenNavigationProp>();
  const navigationAny = useNavigation() as ReturnType<typeof useNavigation>;
  const {
    user,
    refreshUser,
    isAuthenticated,
    needsOnboarding,
    subscriptionPlan,
  } = useAuth();
  const { data: pricingFlag } = useFeatureFlag('pricing_subscription');
  const paymentsKillSwitchOn = isFeatureEnabled(
    pricingFlag,
    'pricing_subscription'
  );
  const { refetch: refetchUsage } = useSubscriptionUsage();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const route = useRoute();

  // Animation states
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [isAdVisible, setIsAdVisible] = useState(false);
  const [welcomeModalVisible, setWelcomeModalVisible] = useState(false);
  const {
    startTour,
    tourStep,
    totalSteps,
    currentStepConfig,
    nextStep,
    skipTour,
  } = useTour();
  const journeyCardRef = useRef<View>(null);
  const badgesRowRef = useRef<View>(null);
  const colorAnalysisBadgeRef = useRef<View>(null);
  const [journeyCardRect, setJourneyCardRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [colorAnalysisButtonRect, setColorAnalysisButtonRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const windowDimensions = Dimensions.get('window');
  const spotlightRect =
    currentStepConfig?.id === 'colourJourney'
      ? journeyCardRect
      : currentStepConfig?.id === 'colorAnalysisButton'
        ? colorAnalysisButtonRect
        : null;
  const showHomeSpotlightTour = !!spotlightRect && !!currentStepConfig;

  const tourHighlightTarget: TourHighlightTarget =
    currentStepConfig?.id === 'colourJourney'
      ? 'badges'
      : currentStepConfig?.id === 'colorAnalysisButton'
        ? 'color-analysis'
        : null;

  const handleTourHighlightRect = useCallback(
    (rect: TourHighlightRect | null) => {
      if (tourHighlightTarget === 'badges') {
        setJourneyCardRect(rect);
        return;
      }
      if (tourHighlightTarget === 'color-analysis') {
        setColorAnalysisButtonRect(rect);
      }
    },
    [tourHighlightTarget]
  );

  // Hybrid "new user" check: used to show Welcome modal first; tour starts only when they tap "Start the Tour"
  const [_hasCompletedTour, setHasCompletedTour] = useState<boolean | null>(
    null
  );
  const autoStartTourAttempted = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || needsOnboarding()) return;
    if (autoStartTourAttempted.current) return;
    autoStartTourAttempted.current = true;
    getHasCompletedTour(true).then((completed) => {
      setHasCompletedTour(completed);
      if (!completed) setWelcomeModalVisible(true);
    });
  }, [isAuthenticated, needsOnboarding]);

  // Legacy home layout: measure spotlight targets when tour steps are active.
  useEffect(() => {
    if (USE_MODERN_HOME) return;
    if (currentStepConfig?.id !== 'colourJourney') {
      setJourneyCardRect(null);
      return;
    }
    const timer = setTimeout(() => {
      journeyCardRef.current?.measureInWindow?.(
        (x: number, y: number, width: number, height: number) => {
          setJourneyCardRect({ x, y, width, height });
        }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [currentStepConfig?.id]);

  useEffect(() => {
    if (USE_MODERN_HOME) return;
    if (currentStepConfig?.id !== 'colorAnalysisButton') {
      setColorAnalysisButtonRect(null);
      return;
    }
    const timer = setTimeout(() => {
      colorAnalysisBadgeRef.current?.measureInWindow?.(
        (x: number, y: number, width: number, height: number) => {
          setColorAnalysisButtonRect({ x, y, width, height });
        }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [currentStepConfig?.id]);

  // Functional state management
  const [userProfile, setUserProfile] = useState<UserProfileInput | null>(null);
  const [, setUserJourneyData] = useState(getInitialUserJourneyData());
  const [personalizedRecommendations, setPersonalizedRecommendations] =
    useState(getPersonalizedRecommendations());
  const [featureUsage, setFeatureUsage] = useState<{ [key: string]: number }>(
    {}
  );
  const [, setLastRefresh] = useState<Date>(new Date());
  // Badges + moodboard hidden for now
  // const [hasMoodBoards, setHasMoodBoards] = useState(false);
  const [hasColorGeneratorData, setHasColorGeneratorData] = useState(false);
  const [hoveredArticle, setHoveredArticle] = useState<string | null>(null);

  // Enable LayoutAnimation on Android
  if (
    Platform.OS === 'android' &&
    UIManager.setLayoutAnimationEnabledExperimental
  ) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
  }

  // Check if this screen is inside MainTabs (tab navigation)
  const isInsideMainTabs = route.name === 'HomeTab';

  useEffect(() => {
    if (typeof document !== 'undefined' && document.title !== undefined) {
      const expectedTitle = 'ColourSense | Home';
      document.title = expectedTitle;

      // Only use MutationObserver if NOT inside MainTabs (standalone screen)
      // MainTabsWithToolbar will handle title when inside tabs
      if (!isInsideMainTabs) {
        const titleObserver = new MutationObserver(() => {
          if (document.title !== expectedTitle) {
            document.title = expectedTitle;
          }
        });

        // Observe changes to document title
        const titleElement = document.querySelector('title');
        if (titleElement) {
          titleObserver.observe(titleElement, {
            childList: true,
            subtree: true,
          });
        }

        // Also observe document.head for title changes
        titleObserver.observe(document.head, {
          childList: true,
          subtree: true,
        });

        return () => {
          titleObserver.disconnect();
        };
      }
    }
  }, [isInsideMainTabs]);

  // Update title when screen comes into focus (for mobile tab navigation)
  useFocusEffect(
    useCallback(() => {
      if (typeof document !== 'undefined' && document.title !== undefined) {
        const expectedTitle = 'ColourSense | Home';
        document.title = expectedTitle;
      }
    }, [])
  );

  const [storedDisplayName, setStoredDisplayName] = useState<string | null>(
    null
  );
  const [storedDisplayNameLoaded, setStoredDisplayNameLoaded] = useState(false);
  useEffect(() => {
    getToken('user_data').then((raw) => {
      if (raw) {
        try {
          const u = JSON.parse(raw);
          const name = u?.name?.trim();
          const emailPart = u?.email ? u.email.split('@')[0] : '';
          if (name)
            setStoredDisplayName(
              name.split(' ')[0].charAt(0).toUpperCase() +
                name.split(' ')[0].slice(1)
            );
          else if (emailPart)
            setStoredDisplayName(
              emailPart.charAt(0).toUpperCase() + emailPart.slice(1)
            );
        } catch {
          // Intentionally ignore parse errors
        }
      }
      setStoredDisplayNameLoaded(true);
    });
  }, []);

  const hasDisplayName = !!(
    user?.name?.trim() ||
    user?.email ||
    (storedDisplayName && storedDisplayName.trim())
  );

  const firstName = user?.name
    ? user.name.split(' ')[0].charAt(0).toUpperCase() +
      user.name.split(' ')[0].slice(1)
    : user?.email
      ? user.email.split('@')[0].charAt(0).toUpperCase() +
        user.email.split('@')[0].slice(1)
      : (storedDisplayName ?? '');

  useEffect(() => {
    if (user && storedDisplayNameLoaded && !hasDisplayName) {
      navigation.replace('Login');
    }
  }, [user, storedDisplayNameLoaded, hasDisplayName, navigation]);

  // Data fetching and initialization
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch color analysis data from API
        let hasCompletedAnalysis = false;
        let season = '';
        let undertone: SeasonUndertone = 'spring';
        let recommendedColors: string[] = [];

        if (user?.id) {
          try {
            const profiles = await getColourProfiles();
            if (profiles && profiles.length > 0) {
              const mostRecentProfile = profiles.sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime()
              )[0];

              hasCompletedAnalysis = true;

              if (mostRecentProfile.recommendations) {
                const recommendations =
                  mostRecentProfile.recommendations as Record<string, unknown>;
                season =
                  recommendations.season ||
                  recommendations.Season ||
                  mostRecentProfile.palette?.type ||
                  '';
                const rawUndertone =
                  recommendations.temperature ||
                  recommendations.undertone ||
                  mostRecentProfile.palette?.type ||
                  'spring';
                undertone = parseUndertoneFromStorage(String(rawUndertone));
              }

              if (
                mostRecentProfile.recommended_colors &&
                mostRecentProfile.recommended_colors.length > 0
              ) {
                recommendedColors = mostRecentProfile.recommended_colors;
              } else if (
                mostRecentProfile.dominant_colours &&
                mostRecentProfile.dominant_colours.length > 0
              ) {
                recommendedColors = mostRecentProfile.dominant_colours.map(
                  (c: { hex?: string } | string) =>
                    typeof c === 'string' ? c : (c.hex ?? c)
                );
              }

              if (!season && mostRecentProfile.palette) {
                const paletteType = mostRecentProfile.palette.type;
                if (
                  paletteType &&
                  ['spring', 'summer', 'autumn', 'winter'].includes(
                    paletteType.toLowerCase()
                  )
                ) {
                  season =
                    paletteType.charAt(0).toUpperCase() +
                    paletteType.slice(1).toLowerCase();
                }
              }
            }
          } catch {
            // Fetch profiles failed silently
          }

          // Moodboard badge checking hidden for now
          // try {
          //   const userMoodBoards = await moodBoardAPI.getUserMoodBoards(parseInt(user.id), { limit: 1 });
          //   setHasMoodBoards(userMoodBoards.items.length > 0);
          // } catch (error) {
          //   console.error('Error checking mood boards:', error);
          //   setHasMoodBoards(false);
          // }
        }

        // Get badge data from user object (stored in database)
        // If timestamp exists, badge is completed
        const hasUsedColorGenerator = !!user?.color_generator_used_at;
        const hasUsedVirtualDrape = !!user?.virtual_drape_used_at;
        setHasColorGeneratorData(hasUsedColorGenerator);

        // Create user profile with API data
        const userProfileData = {
          hasCompletedAnalysis,
          hasCreatedPalette: hasCompletedAnalysis,
          hasUsedVirtualDrape: hasUsedVirtualDrape,
          hasOrganizedWardrobe: false,
          undertone: undertone,
          season: season,
          recommendedColors: recommendedColors,
        };

        // Update state with fetched data
        setUserProfile(userProfileData);
        setUserJourneyData(getInitialUserJourneyData(userProfileData));

        const personalizedRecs =
          getPersonalizedRecommendations(userProfileData);
        if (recommendedColors.length > 0) {
          // Override colors with actual recommended colors from API (show all)
          personalizedRecs.dominantColors = recommendedColors;
          personalizedRecs.recommendedPalette.colors = recommendedColors;
        }
        setPersonalizedRecommendations(personalizedRecs);

        setFeatureUsage({
          'color-analysis': hasCompletedAnalysis ? 1 : 0,
          'virtual-drape': 0,
          'smart-wardrobe': 0,
          'style-consultation': 0,
        });
      } catch {
        // Fallback to default data
        setUserJourneyData(getInitialUserJourneyData());
        setPersonalizedRecommendations(getPersonalizedRecommendations());
      }
    };

    initializeData();
  }, [user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      LayoutAnimation.configureNext({
        duration: 800,
        create: {
          type: LayoutAnimation.Types.easeInEaseOut,
          property: LayoutAnimation.Properties.opacity,
        },
        update: {
          type: LayoutAnimation.Types.easeInEaseOut,
        },
      });

      setLoading(false);

      // Animate entrance
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 800,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start();
    }, 500);

    return () => clearTimeout(timer);
  }, [fadeAnim, slideAnim]);

  useFocusEffect(
    React.useCallback(() => {
      const checkBadges = async () => {
        try {
          await refreshUser();
          refetchUsage();
        } catch {
          // Refresh failed silently
        }

        // Moodboard badge checking hidden for now
        // if (user?.id) {
        //   try {
        //     const userMoodBoards = await moodBoardAPI.getUserMoodBoards(parseInt(user.id), { limit: 1 });
        //     setHasMoodBoards(userMoodBoards.items.length > 0);
        //   } catch (error) {
        //     console.error('Error checking mood boards on focus:', error);
        //   }
        // }

        // Update badge data from user object (stored in database)
        // If timestamp exists, badge is completed
        if (user) {
          setHasColorGeneratorData(!!user.color_generator_used_at);
          // Update userProfile with badge data from user object
          setUserProfile((prev: UserProfileInput | null) => {
            if (!prev) return prev;
            return {
              ...prev,
              hasCompletedAnalysis:
                !!user.color_analysis_used_at || prev.hasCompletedAnalysis,
              hasUsedVirtualDrape:
                !!user.virtual_drape_used_at || prev.hasUsedVirtualDrape,
            };
          });
        }
      };

      checkBadges();
    }, [user, refreshUser, refetchUsage])
  );

  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    setLastRefresh(new Date());

    try {
      refetchUsage();
      // Moodboard badge checking hidden for now
      // if (user?.id) {
      //   try {
      //     const userMoodBoards = await moodBoardAPI.getUserMoodBoards(parseInt(user.id), { limit: 1 });
      //     setHasMoodBoards(userMoodBoards.items.length > 0);
      //   } catch (error) {
      //     console.error('Error checking mood boards:', error);
      //   }
      // }

      const newInsight = getDailyColourInsight(
        userProfile?.undertone || 'spring'
      );
      setUserJourneyData((prev) => ({
        ...prev,
        dailyInsight: newInsight,
      }));

      const newRecommendations = getPersonalizedRecommendations(userProfile);
      setPersonalizedRecommendations(newRecommendations);

      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    } catch {
      // Refresh failed
    } finally {
      setRefreshing(false);
    }
  }, [userProfile, refetchUsage]);

  const handleFeaturePress = (route: string, fromBadge: boolean = false) => {
    try {
      const featureId = route
        .toLowerCase()
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase();
      updateFeatureUsage(featureId);

      // Update user progress based on feature
      if (route === 'ColourAnalysis' && !userProfile?.hasCompletedAnalysis) {
        updateUserProgress(2);
      } else if (
        route === 'VirtualDrapeTest' &&
        !userProfile?.hasUsedVirtualDrape
      ) {
        updateUserProgress(3);
      } else if (
        route === 'SmartWardrobe' &&
        !userProfile?.hasOrganizedWardrobe
      ) {
        updateUserProgress(4);
      }

      // Navigate to feature
      if (onContentChange) {
        // For web layout, map routes to content values
        const contentMap: { [key: string]: string } = {
          ColourAnalysis: 'color-analysis',
          VirtualDrapeTest: 'virtual-drape',
          SmartWardrobe: 'smart-wardrobe',
          StyleConsultation: 'style-consultation',
          ColorGenerator: 'color-generator',
          ColorLibrary: 'color-library',
        };

        const contentValue = contentMap[route] || featureId;
        // Pass fromBadge flag in the content value
        const contentWithFlag = fromBadge
          ? `${contentValue}?fromBadge=true`
          : contentValue;
        onContentChange(contentWithFlag);
      } else if (isMobile) {
        const tabName = getMobileTabForRoute(route);
        if (tabName) {
          try {
            (navigation as ReturnType<typeof useNavigation>).navigate(
              tabName as never
            );
          } catch {
            try {
              navigation.dispatch(CommonActions.navigate({ name: tabName }));
            } catch {
              try {
                (navigationAny as ReturnType<typeof useNavigation>).navigate(
                  tabName as never
                );
              } catch {
                Alert.alert(
                  'Navigation Error',
                  'Could not navigate to the selected feature.'
                );
              }
            }
          }
          if (onTabPress) {
            onTabPress(tabName);
          }
        } else {
          Alert.alert('Navigation Error', 'Feature not available yet.');
        }
      } else {
        // Try NavigationService first, fallback to regular navigation
        const tryNavigation = () => {
          switch (route) {
            case 'ColourAnalysis':
              navigate('MainTabs', { initialTab: 'ColourAnalysis' });
              break;
            case 'VirtualDrapeTest':
              navigate('MainTabs', { initialTab: 'VirtualDrapeTest' });
              break;
            case 'SmartWardrobe':
              navigate('MainTabs', { initialTab: 'SmartWardrobe' });
              break;
            case 'StyleConsultation':
              navigate('StyleConsultationDetail');
              break;
            case 'ColorGenerator':
              navigate('MainTabs', { initialTab: 'ColorGenerator' });
              break;
            case 'ColorLibrary':
              navigate('MainTabs', { initialTab: 'ColorLibrary' });
              break;
            default:
              Alert.alert('Navigation Error', 'Feature not available yet.');
          }
        };

        try {
          tryNavigation();
        } catch {
          try {
            switch (route) {
              case 'ColourAnalysis':
                navigation.navigate(
                  'ColourAnalysis' as keyof RootStackParamList
                );

                break;
              case 'VirtualDrapeTest':
                navigation.navigate(
                  'VirtualDrapeTest' as keyof RootStackParamList
                );

                break;
              case 'SmartWardrobe':
                navigation.navigate(
                  'SmartWardrobe' as keyof RootStackParamList
                );
                break;
              case 'StyleConsultation':
                navigation.navigate(
                  'StyleConsultationDetail' as keyof RootStackParamList
                );
                break;
              case 'ColorGenerator':
                navigation.navigate(
                  'MainTabs' as keyof RootStackParamList,
                  { initialTab: 'ColorGenerator' } as never
                );
                break;
              case 'ColorLibrary':
                navigation.navigate(
                  'MainTabs' as keyof RootStackParamList,
                  { initialTab: 'ColorLibrary' } as never
                );
                break;
              default:
                Alert.alert('Navigation Error', 'Feature not available yet.');
            }
          } catch {
            Alert.alert(
              'Navigation Error',
              'Could not navigate to the selected feature.'
            );
          }
        }
      }

      // Show success feedback
      if (Platform.OS === 'web') {
        // Web feedback could be a toast notification
      } else {
        // Mobile haptic feedback could be added here
      }
    } catch {
      Alert.alert(
        'Navigation Error',
        'Could not navigate to the selected feature.'
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for FAB when uncommented
  const handleCameraPress = () => {
    Alert.alert('Quick Colour Analysis', 'What would you like to do?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Analyse My Colours',
        onPress: () => handleFeaturePress('ColourAnalysis'),
      },
      {
        text: 'Virtual Drape Test',
        onPress: () => handleFeaturePress('VirtualDrapeTest'),
      },
      {
        text: 'Test Navigation',
        onPress: () => {
          try {
            navigate('MainTabs', { initialTab: 'ColourAnalysis' });
          } catch {
            // Intentionally ignore navigation errors
          }
        },
      },
      {
        text: 'Test Web Content Change',
        onPress: () => {
          if (onContentChange) {
            onContentChange('color-analysis');
          }
        },
      },
    ]);
  };

  const updateFeatureUsage = (featureId: string) => {
    setFeatureUsage((prev) => ({
      ...prev,
      [featureId]: (prev[featureId] || 0) + 1,
    }));
  };

  const updateUserProgress = (step: number) => {
    const newProgress = Math.min(step * 25, 100);
    setUserJourneyData((prev) => ({
      ...prev,
      currentStep: step,
      progress: newProgress,
    }));
  };

  const navigateToSettings = () => {
    try {
      navigate('Settings');
    } catch {
      Alert.alert('Navigation Error', 'Could not navigate to settings.');
    }
  };

  const getAchievementOnPress =
    (achievement: { id: string; name: string; route: string }) => () => {
      trackAnalyticsEvent({
        event: 'button_click',
        button_name: achievement.name,
        button_location: 'home-screen-continue-journey',
        feature_type: achievement.id,
        is_completed:
          (achievement.id === 'color-analysis' &&
            (!!user?.color_analysis_used_at ||
              userProfile?.hasCompletedAnalysis)) ||
          (achievement.id === 'virtual-drape' &&
            (!!user?.virtual_drape_used_at ||
              userProfile?.hasUsedVirtualDrape)) ||
          (achievement.id === 'color-generator' &&
            (!!user?.color_generator_used_at || hasColorGeneratorData)) ||
          (achievement.id === 'color-library' && false),
      });
      if (!isMobile) {
        if (achievement.route === 'ColorGenerator') {
          if (onTabPress) onTabPress('ColorGenerator');
          else navigate('MainTabs', { section: 'color-generator' });
        } else if (achievement.route === 'ColourAnalysis') {
          if (onTabPress) onTabPress('ColourAnalysis');
          else navigate('MainTabs', { section: 'color-analysis' });
        } else if (achievement.route === 'VirtualDrapeTest') {
          if (onTabPress) onTabPress('VirtualDrapeTest');
          else navigate('MainTabs', { section: 'virtual-drape-test' });
        } else if (achievement.route === 'ColorLibrary') {
          if (onTabPress) onTabPress('ColorLibrary');
          else navigate('MainTabs', { section: 'color-library' });
        } else {
          handleFeaturePress(achievement.route, true);
        }
      } else {
        const tabName =
          achievement.route === 'ColourAnalysis'
            ? 'ColourAnalysisMobile'
            : achievement.route === 'VirtualDrapeTest'
              ? 'VirtualDrapeTestMobile'
              : achievement.route === 'ColorGenerator'
                ? 'ColorGeneratorMobile'
                : achievement.route === 'ColorLibrary'
                  ? 'ColorLibraryMobile'
                  : null;
        if (tabName) {
          try {
            (navigation as ReturnType<typeof useNavigation>).navigate(
              tabName as never
            );
          } catch {
            try {
              navigation.dispatch(CommonActions.navigate({ name: tabName }));
            } catch {
              try {
                (navigationAny as ReturnType<typeof useNavigation>).navigate(
                  tabName as never
                );
              } catch {
                Alert.alert(
                  'Navigation Error',
                  `Could not navigate to ${tabName}`
                );
              }
            }
          }
        } else {
          handleFeaturePress(achievement.route, true);
        }
      }
    };

  const navigateToProfile = () => {
    try {
      if (!isMobile) {
        // Web: Navigate to ProfileTab
        if (onTabPress) {
          onTabPress('ProfileTab');
        } else {
          navigation.navigate('MainTabs', { initialTab: 'ProfileTab' });
        }
      } else {
        // Mobile: Navigate to ProfileTabMobile
        const navigateToProfileTab = () => {
          try {
            // Try direct navigation first
            (navigation as ReturnType<typeof useNavigation>).navigate(
              'ProfileTabMobile' as never
            );
          } catch {
            try {
              // Try navigating to MainTabs with ProfileTabMobile
              navigation.dispatch(
                CommonActions.navigate({
                  name: 'MainTabs',
                  params: { initialTab: 'ProfileTabMobile' },
                })
              );
            } catch {
              try {
                // Try using navigationAny
                (navigationAny as ReturnType<typeof useNavigation>).navigate(
                  'ProfileTabMobile' as never
                );
              } catch {
                Alert.alert(
                  'Navigation Error',
                  'Could not navigate to profile.'
                );
              }
            }
          }
        };
        navigateToProfileTab();
      }
    } catch {
      Alert.alert('Navigation Error', 'Could not navigate to profile.');
    }
  };

  const renderWelcomeSection = () => {
    if (!hasDisplayName) return null;
    const hasCompletedAnalysis = userProfile?.hasCompletedAnalysis || false;
    const season = userProfile?.season || '';
    const undertone = userProfile?.undertone || 'spring';
    const recommendedPalette = personalizedRecommendations?.recommendedPalette;

    const welcomeMessage =
      hasCompletedAnalysis && season
        ? getSeasonDescription(season, undertone)
        : '';

    return (
      <Animated.View
        style={[
          styles.welcomeSection,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <View
          style={[styles.welcomeGradient, { backgroundColor: colors.card }]}
        >
          {isMobile && (
            <View style={styles.welcomeHeaderTop}>
              <Image
                source={require('../../../assets/coloursense_logo_with_name.png')}
                style={styles.headerLogo}
                resizeMode="contain"
              />
              <TouchableOpacity
                style={[
                  styles.settingsButton,
                  { backgroundColor: colors.background },
                ]}
                onPress={navigateToSettings}
                activeOpacity={0.7}
              >
                <Ionicons
                  name="settings-outline"
                  size={24}
                  color={colors.text}
                />
              </TouchableOpacity>
            </View>
          )}

          <View
            style={[
              styles.welcomeContent,
              isMobile && styles.welcomeContentMobileGap,
            ]}
          >
            <View style={styles.welcomeHeader}>
              <TouchableOpacity
                style={styles.profileContainer}
                onPress={navigateToProfile}
                activeOpacity={0.8}
              >
                <UserAvatar
                  size={Platform.OS === 'web' ? 56 : 64}
                  imageUrl={user?.profileImageUrl}
                  name={user?.name}
                  email={user?.email}
                  showActiveStatus={true}
                  isActive={true}
                />
              </TouchableOpacity>

              <View style={styles.welcomeText}>
                <Text style={[styles.welcomeTitle, { color: colors.text }]}>
                  Welcome, {firstName}! ✨
                </Text>
                <Text
                  style={[styles.welcomeSubtitle, { color: colors.secondary }]}
                >
                  {welcomeMessage}
                </Text>
              </View>
            </View>

            {/* Show recommended palette if user has completed analysis */}
            {hasCompletedAnalysis &&
              recommendedPalette &&
              (() => {
                // Calculate space needed and available
                const numColors = recommendedPalette.colors?.length || 0;
                const colorSize = screenWidth >= 900 ? 40 : 32;
                const colorGap = 8;
                const colorsNeededWidth =
                  numColors > 0
                    ? colorSize * numColors +
                      colorGap * Math.max(0, numColors - 1)
                    : 0;

                const buttonEstimatedWidth = 170;
                const gapBetween = 12;
                const containerSidePadding = 64;

                // For web desktop, the left column is ~67% (flex: 2 out of 3)
                // Account for this when calculating available space
                const effectiveScreenWidth =
                  !isMobile && screenWidth >= 768
                    ? screenWidth * 0.67
                    : screenWidth;

                // Available width = effective screen width - padding - button - gap
                const availableWidth =
                  effectiveScreenWidth -
                  containerSidePadding -
                  buttonEstimatedWidth -
                  gapBetween;

                const needsColumnLayout =
                  screenWidth < 768 || colorsNeededWidth + 20 > availableWidth;

                return (
                  <View
                    style={[
                      styles.paletteSection,
                      {
                        flexDirection: needsColumnLayout ? 'column' : 'row',
                        flexWrap: needsColumnLayout ? 'nowrap' : 'wrap',
                        gap: 12,
                        alignItems: needsColumnLayout ? 'stretch' : 'center',
                        justifyContent: 'flex-start',
                        width: '100%',
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.paletteColorsContainer,
                        {
                          flexDirection: 'row',
                          flexWrap: 'wrap',
                          gap: 8,
                          justifyContent: 'flex-start',
                          flexShrink: needsColumnLayout ? 0 : 1,
                          width: needsColumnLayout ? '100%' : undefined,
                          maxWidth: needsColumnLayout ? '100%' : undefined,
                        },
                      ]}
                    >
                      {recommendedPalette.colors?.map(
                        (color: string, index: number) => (
                          <View
                            key={index}
                            style={[
                              styles.paletteColorBox,
                              {
                                backgroundColor: color,
                                width: colorSize,
                                height: colorSize,
                                minWidth: colorSize,
                                flexShrink: 0,
                              },
                            ]}
                          />
                        )
                      )}
                    </View>
                    <Button
                      title="View My Palette"
                      variant="soft"
                      onPress={() => {
                        // Navigate to ProfileTab (same as sidebar profile button)
                        // Use isMobile to detect mobile (includes mobile browsers)
                        if (!isMobile) {
                          navigate('MainTabs', { section: 'profile' });
                          if (onTabPress) {
                            onTabPress('ProfileTab');
                          }
                        } else {
                          // On mobile: Navigate directly within the tab navigator
                          // MUST use direct tab navigation, NOT navigate('MainTabs') to avoid URL updates

                          const navigateToProfileTab = () => {
                            try {
                              (
                                navigation as ReturnType<typeof useNavigation>
                              ).navigate('ProfileTabMobile' as never);
                              return;
                            } catch {
                              try {
                                navigation.dispatch(
                                  CommonActions.navigate({
                                    name: 'ProfileTabMobile',
                                  })
                                );
                                return;
                              } catch {
                                try {
                                  (
                                    navigationAny as ReturnType<
                                      typeof useNavigation
                                    >
                                  ).navigate('ProfileTabMobile' as never);
                                  return;
                                } catch {
                                  Alert.alert(
                                    'Navigation Error',
                                    'Could not navigate to Profile tab'
                                  );
                                }
                              }
                            }
                          };

                          navigateToProfileTab();
                        }
                      }}
                      icon={
                        <Ionicons
                          name="arrow-forward"
                          size={16}
                          color={palette.outlinePurple}
                        />
                      }
                      iconPosition="right"
                      trackClick={true}
                      trackingLocation="home-screen-welcome-section"
                      trackingData={{
                        button_name: 'View My Palette',
                        button_type: 'view_my_palette',
                        screen: 'home',
                      }}
                      style={{
                        width: needsColumnLayout ? '100%' : 'auto',
                        minWidth: needsColumnLayout ? undefined : 150,
                        flexShrink: 0,
                        alignSelf: needsColumnLayout ? 'stretch' : 'flex-start',
                      }}
                    />
                  </View>
                );
              })()}
          </View>
        </View>
      </Animated.View>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
  const _renderPersonalizedRecommendations = () => (
    <Animated.View
      style={[
        styles.recommendationsSection,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={[styles.sectionTitle, { color: colors.text }]}>
        Personalized for You
      </Text>

      <View style={styles.recommendationsGrid}>
        <View
          style={[styles.recommendationCard, { backgroundColor: colors.card }]}
        >
          <View style={styles.recommendationHeader}>
            <Text style={[styles.recommendationTitle, { color: colors.text }]}>
              Your Season
            </Text>
            <View
              style={[
                styles.seasonBadge,
                { backgroundColor: `${palette.colorGeneratorYellow}20` },
              ]}
            >
              <Text
                style={[
                  styles.seasonText,
                  { color: palette.colorGeneratorYellow },
                ]}
              >
                {personalizedRecommendations.season}
              </Text>
            </View>
          </View>

          <View style={styles.colorPalette}>
            {personalizedRecommendations.dominantColors.map(
              (color: string, index: number) => (
                <View
                  key={index}
                  style={[styles.paletteColor, { backgroundColor: color }]}
                />
              )
            )}
          </View>
        </View>
      </View>
    </Animated.View>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
  const _renderFeatureCards = () => {
    // Check if user has any data (moodboard excluded for now)
    const hasAnyData =
      hasColorGeneratorData ||
      userProfile?.hasCompletedAnalysis ||
      userProfile?.hasUsedVirtualDrape;
    const journeyTitle = hasAnyData
      ? 'Continue Your Colour Journey'
      : 'Start Your Colour Journey';

    return (
      <Animated.View
        style={[
          styles.featuresSection,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          {journeyTitle}
        </Text>

        <View style={styles.featuresGrid}>
          {FEATURE_CARDS.map((feature) => (
            <Animated.View
              key={feature.id}
              style={[
                styles.featureCardContainer,
                {
                  opacity: fadeAnim,
                  transform: [{ translateY: slideAnim }],
                },
              ]}
            >
              <TouchableOpacity
                style={[
                  styles.featureCard,
                  { backgroundColor: colors.card },
                  hoveredCard === feature.id && styles.cardHover,
                  expandedCard === feature.id && styles.cardExpanded,
                ]}
                onPress={() => handleFeaturePress(feature.route)}
                activeOpacity={0.8}
                onPressIn={() => setExpandedCard(feature.id)}
                onPressOut={() => setExpandedCard(null)}
                {...(Platform.OS === 'web' && {
                  onMouseEnter: () => setHoveredCard(feature.id),
                  onMouseLeave: () => setHoveredCard(null),
                })}
              >
                {/* Background Gradient */}
                <LinearGradient
                  colors={feature.bgGradient}
                  style={styles.cardBackgroundGradient}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                />

                {/* Card Header */}
                <View style={styles.cardHeader}>
                  <LinearGradient
                    colors={feature.gradient}
                    style={styles.cardIconContainer}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <Ionicons
                      name={feature.icon as keyof typeof Ionicons.glyphMap}
                      size={24}
                      color="white"
                    />
                  </LinearGradient>

                  <View style={styles.cardMeta}>
                    <View style={styles.difficultyBadge}>
                      <Text
                        style={[
                          styles.difficultyText,
                          { color: feature.accentColor },
                        ]}
                      >
                        {feature.difficulty}
                      </Text>
                    </View>
                    <Text
                      style={[styles.timeEstimate, { color: colors.secondary }]}
                    >
                      {feature.timeEstimate}
                    </Text>
                  </View>
                </View>

                {/* Card Content */}
                <View style={styles.cardContent}>
                  <Text style={[styles.cardTitle, { color: colors.text }]}>
                    {feature.title}
                  </Text>
                  <Text
                    style={[
                      styles.cardSubtitle,
                      { color: feature.accentColor },
                    ]}
                  >
                    {feature.subtitle}
                  </Text>
                  <Text
                    style={[
                      styles.cardDescription,
                      { color: colors.secondary },
                    ]}
                  >
                    {feature.description}
                  </Text>

                  {/* Story Element */}
                  <View style={styles.storyContainer}>
                    <Ionicons
                      name="chatbubble-outline"
                      size={16}
                      color={feature.accentColor}
                    />
                    <Text
                      style={[styles.storyText, { color: colors.secondary }]}
                    >
                      {feature.story}
                    </Text>
                  </View>

                  {/* Features */}
                  <View style={styles.featuresList}>
                    {feature.features.map((feat, idx) => (
                      <View key={idx} style={styles.featureItem}>
                        <Ionicons
                          name="checkmark-circle"
                          size={14}
                          color={feature.accentColor}
                        />
                        <Text
                          style={[
                            styles.featureText,
                            { color: colors.secondary },
                          ]}
                        >
                          {feat}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                {/* Card Footer */}
                <View style={styles.cardFooter}>
                  <View style={styles.popularityIndicator}>
                    <Ionicons
                      name="trending-up"
                      size={14}
                      color={feature.accentColor}
                    />
                    <Text
                      style={[
                        styles.popularityText,
                        { color: feature.accentColor },
                      ]}
                    >
                      {featureUsage[feature.id] || 0} uses today
                    </Text>
                  </View>

                  <Button
                    title={
                      feature.id === 'color-analysis' &&
                      userProfile?.hasCompletedAnalysis
                        ? 'View Results'
                        : feature.id === 'virtual-drape' &&
                            userProfile?.hasUsedVirtualDrape
                          ? 'Try Again'
                          : 'Get Started'
                    }
                    onPress={() => handleFeaturePress(feature.route)}
                    variant="primary"
                    style={{
                      ...styles.cardActionButton,
                      backgroundColor: feature.accentColor,
                    }}
                    textStyle={{ color: colors.background, fontWeight: '600' }}
                    icon={
                      <Ionicons
                        name="arrow-forward"
                        size={16}
                        color={colors.background}
                      />
                    }
                    trackClick={true}
                    trackingLocation="home-screen-feature-cards"
                    trackingData={{
                      button_name: feature.title,
                      button_type: feature.id,
                      screen: 'home',
                    }}
                  />
                </View>
              </TouchableOpacity>
            </Animated.View>
          ))}
        </View>
      </Animated.View>
    );
  };

  // Mock Moodboards data in MoodBoard format - reserved for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _colourStories = [
    {
      id: 'story-1',
      name: 'Found my perfect autumn palette! 🍂',
      description:
        "After months of struggling with my wardrobe, I finally discovered I'm a Deep Autumn. The rich burgundy and olive greens have completely transformed how I feel about my style!",
      images: [
        'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400',
        'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400',
        'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400',
        'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400',
      ],
      colors: ['#8B0000', '#6B8E23', '#D2691E', '#2F4F4F'],
      tags: ['autumn', 'deep-autumn', 'burgundy', 'olive'],
      is_public: true,
      user_name: 'Sarah M.',
      user_profile_image: undefined,
      likes_count: 24,
      created_at: '2024-03-10T12:00:00.000Z',
      is_liked: false,
    },
    {
      id: 'story-2',
      name: 'Virtual drape test changed everything! ✨',
      description:
        'I was skeptical about the virtual drape feature, but it helped me see that cool-toned blues actually work better for me than the warm colors I was wearing.',
      images: [
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
      ],
      colors: ['#4169E1', '#87CEEB', '#191970', '#B0C4DE'],
      tags: ['cool-tones', 'blue', 'virtual-drape', 'discovery'],
      is_public: true,
      user_name: 'Marcus L.',
      user_profile_image: undefined,
      likes_count: 18,
      created_at: '2024-03-07T12:00:00.000Z',
      is_liked: false,
    },
    {
      id: 'story-3',
      name: 'Spring colors are my new favorite! 🌸',
      description:
        "I never thought I could pull off pastels until I did my colour analysis. Now I can't get enough of soft pinks and mint greens!",
      images: [
        'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=400',
        'https://images.unsplash.com/photo-1539109136881-4be0616d9daf?w=400',
      ],
      colors: ['#FFB6C1', '#98D8C8', '#F0E68C', '#FFDAB9'],
      tags: ['spring', 'pastels', 'light-colors', 'soft'],
      is_public: true,
      user_name: 'Emma K.',
      user_profile_image: undefined,
      likes_count: 32,
      created_at: '2024-03-11T12:00:00.000Z',
      is_liked: false,
    },
  ];

  const renderDiscoverAndLearn = () => {
    // Hide Discover & Learn section for first-time users
    // Only show if all badges are completed
    if (!areAllBadgesComplete()) {
      return null;
    }

    // Get featured articles or first 3 articles
    const displayArticles =
      getFeaturedArticles().slice(0, 3).length > 0
        ? getFeaturedArticles().slice(0, 3)
        : blogArticles.slice(0, 3);

    return (
      <Animated.View
        style={[
          styles.discoverSection,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <View style={[styles.discoverCard, { backgroundColor: colors.card }]}>
          <View style={styles.discoverHeader}>
            <View style={styles.discoverTitleContainer}>
              <Ionicons name="book" size={20} color={colors.primary} />
              <Text style={[styles.discoverTitle, { color: colors.text }]}>
                Discover & Learn
              </Text>
            </View>
          </View>

          <View style={styles.learningLinks}>
            {displayArticles.map((article) => {
              const handleArticlePress = () => {
                trackAnalyticsEvent({
                  event: 'button_click',
                  button_name: 'Article Card',
                  button_location: 'home-screen-discover-learn-section',
                  article_id: article.id,
                  article_title: article.title,
                  article_category: article.category,
                  article_read_time: article.readTime,
                });

                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  // Open in new tab on web
                  window.open(`/blog/${article.id}`, '_blank');
                } else {
                  // Navigate normally on mobile
                  navigation.navigate('BlogArticle', { articleId: article.id });
                }
              };

              return (
                <TouchableOpacity
                  key={article.id}
                  style={[
                    styles.learningLink,
                    styles.learningLinkDynamic,
                    hoveredArticle === article.id && styles.learningLinkHovered,
                  ]}
                  onPress={handleArticlePress}
                  activeOpacity={0.7}
                  {...(Platform.OS === 'web' && {
                    onMouseEnter: () => setHoveredArticle(article.id),
                    onMouseLeave: () => setHoveredArticle(null),
                  })}
                >
                  <View style={styles.learningLinkContent}>
                    {article.image && (
                      <Image
                        source={{ uri: article.image }}
                        style={styles.learningLinkImage}
                        resizeMode="cover"
                      />
                    )}
                    <View style={styles.learningLinkTextContainer}>
                      <View style={styles.learningLinkHeader}>
                        <View
                          style={[
                            styles.articleCategoryBadge,
                            { backgroundColor: colors.primary + '15' },
                          ]}
                        >
                          <Text
                            style={[
                              styles.articleCategoryText,
                              { color: colors.primary },
                            ]}
                          >
                            {article.category}
                          </Text>
                        </View>
                        <Text
                          style={[
                            styles.articleReadTime,
                            { color: colors.secondary },
                          ]}
                        >
                          {article.readTime} min
                        </Text>
                      </View>
                      <Text
                        style={[
                          styles.learningLinkText,
                          { color: colors.text },
                        ]}
                        numberOfLines={2}
                        ellipsizeMode="tail"
                      >
                        {article.title}
                      </Text>
                      <Text
                        style={[
                          styles.learningLinkExcerpt,
                          { color: colors.secondary },
                        ]}
                        numberOfLines={1}
                      >
                        {article.excerpt}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>

          <TouchableOpacity
            style={styles.learningFooter}
            onPress={() => {
              trackButtonClick(
                'Read our latest articles',
                'home-screen-discover-learn-section'
              );

              navigation.navigate('BlogList');
            }}
            activeOpacity={0.7}
          >
            <Text style={[styles.learningReadTime, { color: colors.primary }]}>
              Read our latest articles
            </Text>
            <Ionicons name="arrow-forward" size={18} color={colors.primary} />
          </TouchableOpacity>
        </View>
      </Animated.View>
    );
  };

  // Render dummy ad for right column
  const renderAdSlot = () => {
    if (!isAdVisible) return null;

    return (
      <Animated.View
        style={[
          styles.adSlotSection,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <View style={[styles.dummyAd, { backgroundColor: colors.card }]}>
          {/* Ad Header */}
          <View style={styles.adHeader}>
            <View style={styles.adBadge}>
              <Text style={styles.adBadgeText}>Sponsored</Text>
            </View>
            <TouchableOpacity
              style={styles.adCloseButton}
              onPress={() => setIsAdVisible(false)}
            >
              <Ionicons name="close" size={16} color={colors.secondary} />
            </TouchableOpacity>
          </View>

          {/* Ad Content */}
          <View style={styles.adContent}>
            <View style={styles.adImageContainer}>
              <View style={styles.adImagePlaceholder}>
                <Ionicons name="color-palette" size={32} color="#6366F1" />
              </View>
            </View>

            <View style={styles.adTextContent}>
              <Text style={[styles.adTitle, { color: colors.text }]}>
                🎨 ColourSense Pro
              </Text>
              <Text style={[styles.adSubtitle, { color: colors.secondary }]}>
                Unlock unlimited colour analysis, virtual draping, and AI-powered
                style recommendations
              </Text>

              <View style={styles.adOffer}>
                <Text style={styles.adDiscount}>50% OFF</Text>
                <Text style={[styles.adTimeLimit, { color: colors.secondary }]}>
                  Limited Time
                </Text>
              </View>

              <View style={styles.adSocialProof}>
                <Ionicons name="people" size={14} color={colors.primary} />
                <Text
                  style={[styles.adSocialText, { color: colors.secondary }]}
                >
                  2,847 users upgraded this week
                </Text>
              </View>
            </View>
          </View>

          {/* Ad CTA - Upgrade button commented out for now */}
          {/* <TouchableOpacity 
          style={[styles.adCTA, { backgroundColor: colors.primary }]}
          onPress={() => {}}
          activeOpacity={0.8}
        >
          <Text style={styles.adCTAText}>Upgrade to Pro</Text>
          <Ionicons name="arrow-forward" size={16} color="white" />
        </TouchableOpacity> */}

          {/* Ad Progress */}
          <View style={styles.adProgressContainer}>
            <Text style={[styles.adProgressText, { color: colors.secondary }]}>
              ColourSense Pro - Limited time offer
            </Text>
          </View>
        </View>
      </Animated.View>
    );
  };

  /**
   * Returns true only if all 3 badges (color-analysis, virtual-drape, color-generator) are tracked. Moodboard badge hidden for now.
   */
  const areAllBadgesComplete = (): boolean => {
    const colorAnalysisCompleted =
      !!user?.color_analysis_used_at ||
      userProfile?.hasCompletedAnalysis ||
      false;
    const virtualDrapeCompleted =
      !!user?.virtual_drape_used_at ||
      userProfile?.hasUsedVirtualDrape ||
      false;
    const colorGeneratorCompleted =
      !!user?.color_generator_used_at || hasColorGeneratorData;
    return (
      colorAnalysisCompleted && virtualDrapeCompleted && colorGeneratorCompleted
    );
  };

  const renderContinueYourJourney = (
    colorAnalysisBadgeRefArg?: React.RefObject<View>,
    journeyCardRefArg?: React.RefObject<View>
  ) => {
    // Check if user has any data (moodboard excluded for now)
    const hasAnyData =
      hasColorGeneratorData ||
      userProfile?.hasCompletedAnalysis ||
      userProfile?.hasUsedVirtualDrape;
    const journeyTitle = hasAnyData
      ? 'Continue Your Colour Journey'
      : 'Start Your Colour Journey';

    // Moodboard achievement hidden for now
    const achievements = [
      {
        id: 'color-analysis',
        name: 'Colour Analysis',
        icon: 'color-palette' as const,
        color: '#FF6B9D',
        route: 'ColourAnalysis',
      },
      {
        id: 'virtual-drape',
        name: 'Virtual Drape',
        icon: 'shirt' as const,
        color: '#4ECDC4',
        route: 'VirtualDrapeTest',
      },
      {
        id: 'color-generator',
        name: 'Colour Generator',
        icon: 'sparkles' as const,
        color: palette.colorGeneratorYellow,
        route: 'ColorGenerator',
      },
      // { id: 'moodboard', name: 'Mood Board', icon: 'images' as const, color: '#764ba2', route: 'MoodBoard' },
    ];

    return (
      <Animated.View
        style={[
          styles.continueJourneyCard,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <View
          ref={journeyCardRefArg}
          collapsable={false}
          style={[
            styles.continueJourneyContent,
            { backgroundColor: colors.card },
          ]}
        >
          <View style={styles.continueJourneyHeader}>
            <Ionicons name="rocket" size={24} color={colors.primary} />
            <View style={styles.continueJourneyText}>
              <Text
                style={[styles.continueJourneyTitle, { color: colors.text }]}
              >
                {journeyTitle}
              </Text>
            </View>
          </View>

          <Text
            style={[
              styles.continueJourneyDescription,
              { color: colors.secondary },
            ]}
          >
            Embark on your colour quest and unlock legendary abilities to become
            the ultimate style champion!
          </Text>

          <View
            style={[
              styles.achievementsContainer,
              Platform.OS !== 'web' && styles.achievementsContainerMobile,
            ]}
          >
            {achievements.map((achievement) => {
              const isCompleted = (() => {
                switch (achievement.id) {
                  case 'color-analysis':
                    return (
                      !!user?.color_analysis_used_at ||
                      userProfile?.hasCompletedAnalysis ||
                      false
                    );
                  case 'virtual-drape':
                    return (
                      !!user?.virtual_drape_used_at ||
                      userProfile?.hasUsedVirtualDrape ||
                      false
                    );
                  case 'color-generator':
                    return (
                      !!user?.color_generator_used_at || hasColorGeneratorData
                    );
                  default:
                    return false;
                }
              })();

              const handleBadgePress = () => {
                trackAnalyticsEvent({
                  event: 'button_click',
                  button_name: achievement.name,
                  button_location: 'home-screen-continue-journey',
                  feature_type: achievement.id,
                  is_completed: isCompleted,
                });

                // Use isMobile for navigation logic (includes mobile browsers, not just native)
                // isMobile is true when Platform.OS !== 'web' OR screenWidth < 768
                if (!isMobile) {
                  // Desktop web navigation - use onTabPress when available (handles URL updates)
                  // This prevents React Navigation from appending tab names to URLs
                  if (achievement.route === 'ColorGenerator') {
                    if (onTabPress) {
                      // onTabPress will trigger handleTabChange which updates the URL correctly
                      onTabPress('ColorGenerator');
                    } else {
                      // Fallback: navigate only if onTabPress not available
                      navigate('MainTabs', { section: 'color-generator' });
                    }
                    // Moodboard hidden for now
                    // } else if (achievement.route === 'MoodBoard') {
                    //   if (onTabPress) {
                    //     onTabPress('MoodBoardTab');
                    //   } else {
                    //     navigate('MainTabs', { section: 'mood-boards' });
                    //   }
                  } else if (achievement.route === 'ColourAnalysis') {
                    if (onTabPress) {
                      onTabPress('ColourAnalysis');
                    } else {
                      navigate('MainTabs', { section: 'color-analysis' });
                    }
                  } else if (achievement.route === 'VirtualDrapeTest') {
                    if (onTabPress) {
                      onTabPress('VirtualDrapeTest');
                    } else {
                      navigate('MainTabs', { section: 'virtual-drape-test' });
                    }
                  } else {
                    handleFeaturePress(achievement.route, true);
                  }
                } else {
                  // Mobile navigation: Navigate directly to tabs within the tab navigator
                  // On mobile, we MUST use direct tab navigation, NOT navigate('MainTabs')
                  // because navigate('MainTabs') triggers React Navigation linking which updates the URL

                  const navigateToMobileTab = (tabName: string) => {
                    // Method 1: Direct navigation within tab navigator
                    // When inside a tab screen, useNavigation() returns the tab navigator's navigation
                    try {
                      (navigation as ReturnType<typeof useNavigation>).navigate(
                        tabName as never
                      );
                      return;
                    } catch {
                      // Method 2: Try using CommonActions navigate
                      try {
                        navigation.dispatch(
                          CommonActions.navigate({
                            name: tabName,
                          })
                        );
                        return;
                      } catch {
                        // Method 3: Try navigationAny (backup navigation object)
                        try {
                          (
                            navigationAny as ReturnType<typeof useNavigation>
                          ).navigate(tabName as never);
                          return;
                        } catch {
                          Alert.alert(
                            'Navigation Error',
                            `Could not navigate to ${tabName}`
                          );
                        }
                      }
                    }
                  };

                  // Map routes to mobile tab names from BottomTabParamList
                  if (achievement.route === 'ColourAnalysis') {
                    navigateToMobileTab('ColourAnalysisMobile');
                  } else if (achievement.route === 'VirtualDrapeTest') {
                    navigateToMobileTab('VirtualDrapeTestMobile');
                    // } else if (achievement.route === 'MoodBoard') {
                    //   navigateToMobileTab('MoodBoardTabMobile');
                  } else if (achievement.route === 'ColorGenerator') {
                    // Navigate to ColorGenerator mobile tab
                    navigateToMobileTab('ColorGeneratorMobile');
                  } else {
                    // For other routes, use handleFeaturePress
                    handleFeaturePress(achievement.route, true);
                  }
                }
              };

              const badgeData: BadgeData = {
                id: achievement.id,
                name: achievement.name,
                icon: achievement.icon,
                color: achievement.color,
                route: achievement.route,
                completed: isCompleted,
                onPress: handleBadgePress,
              };

              return (
                <View
                  key={achievement.id}
                  ref={
                    achievement.id === 'color-analysis'
                      ? colorAnalysisBadgeRefArg
                      : undefined
                  }
                  collapsable={achievement.id !== 'color-analysis'}
                  style={
                    Platform.OS !== 'web'
                      ? styles.badgeWrapperMobile
                      : undefined
                  }
                >
                  <Badge
                    badge={badgeData}
                    size={Platform.OS === 'web' ? 'medium' : 'small'}
                    showLabel={true}
                  />
                </View>
              );
            })}
          </View>
        </View>
      </Animated.View>
    );
  };

  // Moodboards section hidden for now
  const renderColourStories = () => null;

  const hasAnyData =
    hasColorGeneratorData ||
    userProfile?.hasCompletedAnalysis ||
    userProfile?.hasUsedVirtualDrape;
  const journeyTitle = hasAnyData
    ? 'Continue Your Colour Journey'
    : 'Start Your Colour Journey';

  const modernAchievements: AchievementItem[] = [
    {
      id: 'color-analysis',
      name: 'Colour Analysis',
      icon: 'color-palette',
      color: '#FF6B9D',
      route: 'ColourAnalysis',
      completed:
        !!user?.color_analysis_used_at ||
        userProfile?.hasCompletedAnalysis ||
        false,
      onPress: getAchievementOnPress({
        id: 'color-analysis',
        name: 'Colour Analysis',
        route: 'ColourAnalysis',
      }),
    },
    {
      id: 'virtual-drape',
      name: 'Virtual Drape',
      icon: 'shirt',
      color: '#4ECDC4',
      route: 'VirtualDrapeTest',
      completed:
        !!user?.virtual_drape_used_at ||
        userProfile?.hasUsedVirtualDrape ||
        false,
      onPress: getAchievementOnPress({
        id: 'virtual-drape',
        name: 'Virtual Drape',
        route: 'VirtualDrapeTest',
      }),
    },
    {
      id: 'color-generator',
      name: 'Colour Generator',
      icon: 'sparkles',
      color: palette.colorGeneratorYellow,
      route: 'ColorGenerator',
      completed: !!user?.color_generator_used_at || hasColorGeneratorData,
      onPress: getAchievementOnPress({
        id: 'color-generator',
        name: 'Colour Generator',
        route: 'ColorGenerator',
      }),
    },
    {
      id: 'color-library',
      name: 'Colour Library',
      icon: 'library',
      color: '#8B5CF6',
      route: 'ColorLibrary',
      completed: false,
      onPress: getAchievementOnPress({
        id: 'color-library',
        name: 'Colour Library',
        route: 'ColorLibrary',
      }),
    },
  ];

  const getFeatureButtonLabel = (featureId: string) => {
    if (featureId === 'color-analysis' && userProfile?.hasCompletedAnalysis)
      return 'View Results';
    if (featureId === 'virtual-drape' && userProfile?.hasUsedVirtualDrape)
      return 'Try Again';
    return 'Get Started';
  };

  const discoverArticlesForModern =
    getFeaturedArticles().slice(0, 3).length > 0
      ? getFeaturedArticles().slice(0, 3)
      : blogArticles.slice(0, 3);

  const handleArticlePressModern = (articleId: string) => {
    trackAnalyticsEvent({
      event: 'button_click',
      button_name: 'Article Card',
      button_location: 'home-screen-discover-learn-section',
      article_id: articleId,
    });
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(`/blog/${articleId}`, '_blank');
    } else {
      navigation.navigate('BlogArticle', { articleId });
    }
  };

  if (loading) {
    return (
      <View
        style={[
          styles.container,
          {
            backgroundColor: colors.background,
            justifyContent: 'center',
            alignItems: 'center',
          },
        ]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text
          style={[
            styles.loadingText,
            { color: colors.secondary, marginTop: 16 },
          ]}
        >
          Loading your colour journey...
        </Text>
      </View>
    );
  }

  if (USE_MODERN_HOME) {
    const welcomeMessageModern =
      userProfile?.hasCompletedAnalysis && userProfile?.season
        ? getSeasonDescription(
            userProfile.season,
            userProfile.undertone || 'warm'
          )
        : '';
    return (
      <View
        style={[
          styles.container,
          styles.containerModern,
          isMobile && styles.containerMobile,
          { backgroundColor: colors.background },
        ]}
      >
        <ModernHomeContent
          colors={colors}
          firstName={firstName}
          welcomeMessage={welcomeMessageModern}
          hasCompletedAnalysis={userProfile?.hasCompletedAnalysis ?? false}
          recommendedPalette={
            personalizedRecommendations?.recommendedPalette ?? null
          }
          user={user}
          onViewPalette={() => {
            if (!isMobile) {
              navigate('MainTabs', { section: 'profile' });
              if (onTabPress) onTabPress('ProfileTab');
            } else {
              try {
                (navigation as ReturnType<typeof useNavigation>).navigate(
                  'ProfileTabMobile' as never
                );
              } catch {
                try {
                  navigation.dispatch(
                    CommonActions.navigate({ name: 'ProfileTabMobile' })
                  );
                } catch {
                  try {
                    (
                      navigationAny as ReturnType<typeof useNavigation>
                    ).navigate('ProfileTabMobile' as never);
                  } catch {
                    Alert.alert(
                      'Navigation Error',
                      'Could not navigate to Profile tab'
                    );
                  }
                }
              }
            }
          }}
          onProfile={navigateToProfile}
          onSettings={navigateToSettings}
          journeyTitle={journeyTitle}
          achievements={modernAchievements}
          featureCards={FEATURE_CARDS as FeatureCardConfig[]}
          onFeaturePress={(route) => handleFeaturePress(route)}
          getFeatureButtonLabel={getFeatureButtonLabel}
          discoverArticles={discoverArticlesForModern}
          onArticlePress={handleArticlePressModern}
          onViewAllArticles={() => navigation.navigate('BlogList')}
          showUpgradeCta={subscriptionPlan === 'free' && !paymentsKillSwitchOn}
          onUpgradePress={() => navigation.navigate('Pricing')}
          isRefreshing={refreshing}
          onRefresh={onRefresh}
          colorAnalysisBadgeRef={colorAnalysisBadgeRef}
          journeyCardRef={journeyCardRef}
          badgesRowRef={badgesRowRef}
          tourHighlightTarget={tourHighlightTarget}
          onTourHighlightRect={handleTourHighlightRect}
        />
        <WelcomeModal
          visible={welcomeModalVisible}
          onClose={() => {
            setWelcomeModalVisible(false);
            markTourCompleted(user?.id ?? null);
          }}
          onStartTour={() => {
            setWelcomeModalVisible(false);
            startTour();
          }}
          onMaybeLater={() => {
            setWelcomeModalVisible(false);
            markTourCompleted(user?.id ?? null);
          }}
        />
        <Modal
          visible={!!showHomeSpotlightTour}
          transparent
          animationType="fade"
          onRequestClose={skipTour}
          statusBarTranslucent={Platform.OS !== 'web'}
        >
          <View style={styles.tourJourneyModalContent}>
            {spotlightRect && (
              <>
                {Platform.OS === 'web' ? (
                  <Pressable
                    style={[
                      styles.tourJourneyOverlayStrip,
                      StyleSheet.absoluteFillObject,
                    ]}
                    onPress={skipTour}
                  >
                    <View
                      pointerEvents="none"
                      style={(() => {
                        const padding = 2;
                        const radius =
                          currentStepConfig?.id === 'colourJourney' ? 12 : 16;
                        return {
                          position: 'absolute',
                          left: spotlightRect.x - padding,
                          top: spotlightRect.y - padding,
                          width: spotlightRect.width + padding * 2,
                          height: spotlightRect.height + padding * 2,
                          borderRadius: radius,
                          boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                        } as const;
                      })()}
                    />
                  </Pressable>
                ) : (
                  <>
                    <Pressable
                      style={[
                        styles.tourJourneyOverlayStrip,
                        {
                          top: 0,
                          left: 0,
                          right: 0,
                          height: spotlightRect.y,
                        },
                      ]}
                      onPress={skipTour}
                    />
                    <Pressable
                      style={[
                        styles.tourJourneyOverlayStrip,
                        {
                          top: spotlightRect.y + spotlightRect.height,
                          left: 0,
                          right: 0,
                          bottom: 0,
                        },
                      ]}
                      onPress={skipTour}
                    />
                    <Pressable
                      style={[
                        styles.tourJourneyOverlayStrip,
                        {
                          top: spotlightRect.y,
                          left: 0,
                          width: spotlightRect.x,
                          height: spotlightRect.height,
                        },
                      ]}
                      onPress={skipTour}
                    />
                    <Pressable
                      style={[
                        styles.tourJourneyOverlayStrip,
                        {
                          top: spotlightRect.y,
                          left: spotlightRect.x + spotlightRect.width,
                          right: 0,
                          height: spotlightRect.height,
                        },
                      ]}
                      onPress={skipTour}
                    />
                  </>
                )}
                <View
                  style={[
                    styles.tourJourneySpotlightBorder,
                    {
                      position: 'absolute',
                      left: spotlightRect.x - 2,
                      top: spotlightRect.y - 2,
                      width: spotlightRect.width + 4,
                      height: spotlightRect.height + 4,
                    },
                  ]}
                />
                <View
                  style={[
                    styles.tourJourneyTooltipWrap,
                    isMobile && styles.tourJourneyTooltipWrapMobile,
                    getTourJourneyTooltipWrapStyle(
                      currentStepConfig?.placement ?? 'bottom',
                      spotlightRect,
                      windowDimensions.height,
                      windowDimensions.width,
                      isMobile,
                      insets.bottom
                    ),
                  ]}
                >
                  {currentStepConfig && (
                    <TourTooltipContent
                      title={currentStepConfig.title}
                      body={currentStepConfig.body}
                      onNext={nextStep}
                      onSkip={skipTour}
                      isLastStep={tourStep === totalSteps}
                      startActionLabel={
                        currentStepConfig.id === 'colorAnalysisButton'
                          ? 'Start'
                          : undefined
                      }
                      onStartAction={
                        currentStepConfig.id === 'colorAnalysisButton'
                          ? async () => {
                              trackTutorialEvent('step_start_action', {
                                tutorial_step_id: 'colorAnalysisButton',
                                tutorial_step_index: tourStep,
                                tutorial_step_title: currentStepConfig.title,
                              });
                              await AsyncStorage.setItem(
                                SHOW_COLOR_ANALYSIS_WIZARD_KEY,
                                '1'
                              );
                              navigate('MainTabs', {
                                initialTab: 'ColourAnalysis',
                              });
                              skipTour();
                            }
                          : undefined
                      }
                    />
                  )}
                </View>
              </>
            )}
          </View>
        </Modal>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {isMobile ? (
        <View style={styles.mobileContainer}>
          <FlatList
            data={[
              { id: 'welcome', type: 'welcome' },
              { id: 'continue', type: 'continue' },
              { id: 'colourStories', type: 'colourStories' },
              { id: 'discover', type: 'discover' },
              { id: 'ads', type: 'ads' },
            ]}
            ListHeaderComponent={null}
            renderItem={({ item }) => {
              switch (item.type) {
                case 'welcome':
                  return renderWelcomeSection();
                case 'continue':
                  return (
                    <View>
                      {renderContinueYourJourney(
                        colorAnalysisBadgeRef,
                        journeyCardRef
                      )}
                    </View>
                  );
                case 'discover':
                  return renderDiscoverAndLearn();
                case 'colourStories':
                  return renderColourStories();
                case 'ads':
                  return renderAdSlot();
                default:
                  return null;
              }
            }}
            keyExtractor={(item) => item.id}
            contentContainerStyle={{
              paddingBottom: 100 + insets.bottom,
            }}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
          />

          {/* Enhanced Floating Action Button - Hidden */}
          {/* <View style={styles.fabContainer}>
            <TouchableOpacity
              style={[styles.fab, { backgroundColor: colors.primary }]}
              onPress={handleCameraPress}
              activeOpacity={0.8}
            >
              <Ionicons name='camera' size={28} color='white' />
            </TouchableOpacity>
          </View> */}
        </View>
      ) : (
        <ScrollView
          style={styles.webContainer}
          contentContainerStyle={styles.webContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {/* Main Layout - 2/3 and 1/3 split */}
          <View style={styles.webMainLayout}>
            {/* Left Column - 2/3 */}
            <View style={styles.webLeftColumn}>
              {renderWelcomeSection()}
              {renderContinueYourJourney(colorAnalysisBadgeRef, journeyCardRef)}
              {renderColourStories()}
            </View>

            {/* Right Column - 1/3 */}
            <View style={styles.webRightColumn}>
              {renderDiscoverAndLearn()}
              {renderAdSlot()}
            </View>
          </View>
        </ScrollView>
      )}

      <WelcomeModal
        visible={welcomeModalVisible}
        onClose={() => {
          setWelcomeModalVisible(false);
          markTourCompleted(user?.id ?? null);
        }}
        onStartTour={() => {
          setWelcomeModalVisible(false);
          startTour();
        }}
        onMaybeLater={() => {
          setWelcomeModalVisible(false);
          markTourCompleted(user?.id ?? null);
        }}
      />

      {/* Step 2 (Your Badges) & Step 3 (Color Analysis button) – spotlight (same as sidebar) */}
      <Modal
        visible={!!showHomeSpotlightTour}
        transparent
        animationType="fade"
        onRequestClose={skipTour}
        statusBarTranslucent={Platform.OS !== 'web'}
      >
        <View style={styles.tourJourneyModalContent}>
          {spotlightRect && (
            <>
              {Platform.OS === 'web' ? (
                // Web: use CSS box-shadow trick to create a rounded transparent spotlight
                <Pressable
                  style={[
                    styles.tourJourneyOverlayStrip,
                    StyleSheet.absoluteFillObject,
                  ]}
                  onPress={skipTour}
                >
                  <View
                    pointerEvents="none"
                    style={(() => {
                      const padding = 2;
                      const radius =
                        currentStepConfig?.id === 'colourJourney' ? 12 : 16;
                      return {
                        position: 'absolute',
                        left: spotlightRect.x - padding,
                        top: spotlightRect.y - padding,
                        width: spotlightRect.width + padding * 2,
                        height: spotlightRect.height + padding * 2,
                        borderRadius: radius,
                        // Large box-shadow draws the dimmed area outside the rounded rect
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore - boxShadow is web-only
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                      } as const;
                    })()}
                  />
                </Pressable>
              ) : (
                <>
                  {/* Native: original four strips spotlight */}
                  <Pressable
                    style={[
                      styles.tourJourneyOverlayStrip,
                      {
                        top: 0,
                        left: 0,
                        right: 0,
                        height: spotlightRect.y,
                      },
                    ]}
                    onPress={skipTour}
                  />
                  <Pressable
                    style={[
                      styles.tourJourneyOverlayStrip,
                      {
                        top: spotlightRect.y + spotlightRect.height,
                        left: 0,
                        right: 0,
                        bottom: 0,
                      },
                    ]}
                    onPress={skipTour}
                  />
                  <Pressable
                    style={[
                      styles.tourJourneyOverlayStrip,
                      {
                        top: spotlightRect.y,
                        left: 0,
                        width: spotlightRect.x,
                        height: spotlightRect.height,
                      },
                    ]}
                    onPress={skipTour}
                  />
                  <Pressable
                    style={[
                      styles.tourJourneyOverlayStrip,
                      {
                        top: spotlightRect.y,
                        left: spotlightRect.x + spotlightRect.width,
                        right: 0,
                        height: spotlightRect.height,
                      },
                    ]}
                    onPress={skipTour}
                  />
                </>
              )}
              {/* Subtle border around spotlit target (same style as sidebar highlight) */}
              <View
                style={[
                  styles.tourJourneySpotlightBorder,
                  {
                    position: 'absolute',
                    left: spotlightRect.x - 2,
                    top: spotlightRect.y - 2,
                    width: spotlightRect.width + 4,
                    height: spotlightRect.height + 4,
                  },
                ]}
              />
              <View
                style={[
                  styles.tourJourneyTooltipWrap,
                  isMobile && styles.tourJourneyTooltipWrapMobile,
                  getTourJourneyTooltipWrapStyle(
                    currentStepConfig?.placement ?? 'bottom',
                    spotlightRect,
                    windowDimensions.height,
                    windowDimensions.width,
                    isMobile,
                    insets.bottom
                  ),
                ]}
              >
                {currentStepConfig && (
                  <TourTooltipContent
                    title={currentStepConfig.title}
                    body={currentStepConfig.body}
                    onNext={nextStep}
                    onSkip={skipTour}
                    isLastStep={tourStep === totalSteps}
                    startActionLabel={
                      currentStepConfig.id === 'colorAnalysisButton'
                        ? 'Start'
                        : undefined
                    }
                    onStartAction={
                      currentStepConfig.id === 'colorAnalysisButton'
                        ? async () => {
                            trackTutorialEvent('step_start_action', {
                              tutorial_step_id: 'colorAnalysisButton',
                              tutorial_step_index: tourStep,
                              tutorial_step_title: currentStepConfig.title,
                            });
                            await AsyncStorage.setItem(
                              SHOW_COLOR_ANALYSIS_WIZARD_KEY,
                              '1'
                            );
                            navigate('MainTabs', {
                              initialTab: 'ColourAnalysis',
                            });
                            skipTour();
                          }
                        : undefined
                    }
                  />
                )}
              </View>
            </>
          )}
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
  },
  containerModern: {
    paddingHorizontal: 0,
  },
  containerMobile: {
    paddingTop: 4,
    paddingBottom: 8,
  },

  // Mobile Styles
  mobileContainer: {
    flex: 1,
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerMobile: {
    borderRadius: 16,
    marginHorizontal: 8,
    marginTop: 8,
    marginBottom: 8,
    borderBottomWidth: 0,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLogo: {
    width: 180,
    height: 45,
  },
  settingsButton: {
    padding: 12,
    borderRadius: 12,
    marginBottom: Platform.OS === 'web' ? 0 : 16,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },

  // Tour step 2 (Your Badges / Start Your Colour Journey) – spotlight like sidebar
  tourJourneyModalContent: {
    flex: 1,
  },
  tourJourneyOverlayStrip: {
    position: 'absolute',
    backgroundColor: 'rgba(0,0,0,0.5)',
    ...(Platform.OS === 'web' && {
      backgroundColor: 'transparent',
      cursor: 'pointer',
    }),
  },
  tourJourneySpotlightBorder: {
    pointerEvents: 'none',
    borderRadius: Platform.OS === 'web' ? 12 : 16,
  },
  tourJourneyTooltipWrap: {
    position: 'absolute',
    zIndex: 1,
    ...(Platform.OS === 'web' && { maxWidth: 400 }),
  },
  tourJourneyTooltipWrapMobile: {
    maxWidth: undefined,
  },

  // Welcome Section
  welcomeSection: {
    marginTop: Platform.OS === 'web' ? 0 : 20,
    marginBottom: Platform.OS === 'web' ? 16 : 20,
    marginHorizontal: 8,
    borderRadius: 16,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  welcomeGradient: {
    padding: Platform.OS === 'web' ? 16 : 20,
    borderRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
  },
  welcomeContent: {
    gap: Platform.OS === 'web' ? 4 : 20,
    marginTop: Platform.OS === 'web' ? 0 : 32,
  },
  welcomeContentMobileGap: {
    marginTop: 24,
  },
  welcomeHeaderTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Platform.OS === 'web' ? 0 : 32,
    paddingBottom: Platform.OS === 'web' ? 0 : 24,
    borderBottomWidth: Platform.OS === 'web' ? 0 : 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  welcomeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 12 : 14,
  },
  profileContainer: {
    position: 'relative',
  },
  onlineIndicator: {
    position: 'absolute',
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: palette.cyanLimeGreen,
    borderWidth: 3,
    borderColor: palette.white,
    bottom: 2,
    right: 2,
  },
  welcomeText: {
    flex: 1,
  },
  welcomeTitle: {
    fontSize: Platform.OS === 'web' ? 16 : 18,
    fontWeight: '700',
    marginBottom: Platform.OS === 'web' ? 2 : 4,
  },
  welcomeSubtitle: {
    fontSize: Platform.OS === 'web' ? 14 : 13,
    lineHeight: Platform.OS === 'web' ? 18 : 18,
    fontWeight: Platform.OS === 'web' ? '400' : '500',
  },
  paletteSection: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: Platform.OS === 'web' ? 10 : 16,
    paddingTop: Platform.OS === 'web' ? 12 : 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
    gap: Platform.OS === 'web' ? 0 : 16,
  },
  paletteColorsContainer: {
    flexDirection: 'row',
    gap: Platform.OS === 'web' ? 8 : 10,
    flex: Platform.OS === 'web' ? 1 : 0,
    marginRight: Platform.OS === 'web' ? 12 : 0,
    flexWrap: 'wrap',
    maxWidth: '100%',
    justifyContent: Platform.OS === 'web' ? 'flex-start' : 'center',
  },
  paletteColorBox: {
    width: Platform.OS === 'web' ? 40 : 36,
    height: Platform.OS === 'web' ? 40 : 36,
    borderRadius: Platform.OS === 'web' ? 8 : 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    minWidth: Platform.OS === 'web' ? 40 : 36,
  },
  viewPaletteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Platform.OS === 'web' ? 10 : 14,
    paddingHorizontal: Platform.OS === 'web' ? 16 : 20,
    borderRadius: Platform.OS === 'web' ? 8 : 12,
    gap: 6,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    width: Platform.OS === 'web' ? 'auto' : '100%',
    marginTop: Platform.OS === 'web' ? 0 : 0,
  },
  viewPaletteButtonText: {
    color: palette.white,
    fontSize: Platform.OS === 'web' ? 14 : 14,
    fontWeight: '600',
  },

  // Journey Progress
  journeyProgress: {
    gap: 2,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressTitle: {
    fontSize: Platform.OS === 'web' ? 16 : 12,
    fontWeight: '600',
  },
  progressSubtitle: {
    fontSize: Platform.OS === 'web' ? 14 : 10,
    lineHeight: Platform.OS === 'web' ? 18 : 14,
  },
  progressBar: {
    gap: 4,
  },
  progressTrack: {
    height: 8,
    backgroundColor: palette.lightGray,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: palette.brightBlue,
    borderRadius: 4,
  },
  progressText: {
    fontSize: 12,
    textAlign: 'center',
  },
  nextMilestone: {
    fontSize: 12,
    fontStyle: 'italic',
    lineHeight: 16,
  },

  // Recommendations Section
  recommendationsSection: {
    margin: 8,
  },
  section: {
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: Platform.OS === 'web' ? 22 : 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  recommendationsGrid: {
    gap: 8,
  },
  recommendationCard: {
    borderRadius: 12,
    padding: 16,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  recommendationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  seasonBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  seasonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  colorPalette: {
    flexDirection: 'row',
    height: 40,
    borderRadius: 8,
    overflow: 'hidden',
  },
  paletteColor: {
    flex: 1,
  },

  // Features Section
  featuresSection: {
    margin: 8,
    marginTop: 4,
  },
  featuresGrid: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  featureCardContainer: {
    flex: 1,
    marginBottom: 8,
  },
  featureCard: {
    borderRadius: 12,
    padding: 16,
    minHeight: 180,
    maxHeight: 320,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
    position: 'relative',
    overflow: 'hidden',
    marginBottom: 8,
  },
  cardBackgroundGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 20,
  },
  cardHover: {
    transform: [{ scale: 1.02 }],
    shadowOpacity: 0.25,
    shadowRadius: 24,
    elevation: 12,
  },
  cardExpanded: {
    transform: [{ scale: 0.98 }],
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  cardIconContainer: {
    width: Platform.OS === 'web' ? 56 : 40,
    height: Platform.OS === 'web' ? 56 : 40,
    borderRadius: Platform.OS === 'web' ? 28 : 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardMeta: {
    alignItems: 'flex-end',
    gap: 8,
  },
  difficultyBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  difficultyText: {
    fontSize: 11,
    fontWeight: '700',
  },
  timeEstimate: {
    fontSize: 13,
    fontWeight: '500',
  },
  cardContent: {
    gap: 8,
    flex: 1,
  },
  cardTitle: {
    fontSize: Platform.OS === 'web' ? 16 : 10,
    fontWeight: '700',
    lineHeight: Platform.OS === 'web' ? 20 : 14,
  },
  cardSubtitle: {
    fontSize: Platform.OS === 'web' ? 14 : 9,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  cardDescription: {
    fontSize: Platform.OS === 'web' ? 12 : 9,
    lineHeight: Platform.OS === 'web' ? 16 : 12,
    opacity: 0.9,
  },
  storyContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
    padding: 12,
    backgroundColor: 'rgba(255,255,255,0.6)',
    borderRadius: 12,
    marginTop: 6,
  },
  storyText: {
    fontSize: 12,
    lineHeight: 16,
    flex: 1,
    fontStyle: 'italic',
  },
  featuresList: {
    gap: 6,
    marginTop: 6,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  featureText: {
    fontSize: Platform.OS === 'web' ? 12 : 9,
    lineHeight: Platform.OS === 'web' ? 16 : 12,
    fontWeight: '500',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  popularityIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  popularityText: {
    fontSize: 12,
    fontWeight: '600',
  },
  cardAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  cardActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  cardActionContainer: {
    marginTop: 12,
  },
  difficultyContainer: {
    alignItems: 'flex-end',
    gap: 6,
  },
  actionText: {
    fontSize: 12,
    fontWeight: '700',
  },

  // Discover and Learn Section
  discoverSection: {
    marginBottom: Platform.OS === 'web' ? 16 : 16,
    marginHorizontal: 8,
  },
  discoverCard: {
    borderRadius: Platform.OS === 'web' ? 12 : 16,
    padding: Platform.OS === 'web' ? 20 : 18,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
  },
  discoverHeader: {
    marginBottom: 16,
  },
  discoverTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  discoverTitle: {
    fontSize: Platform.OS === 'web' ? 16 : 18,
    fontWeight: '700',
  },
  learningLinks: {
    gap: 12,
  },
  learningLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 6,
  },
  learningLinkText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
    lineHeight: 20,
    flexShrink: 1,
  },
  learningLinkDynamic: {
    paddingVertical: 14,
    paddingHorizontal: 12,
    marginBottom: 0,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
    backgroundColor: 'transparent',
    transition: 'all 0.2s ease',
  },
  learningLinkHovered: {
    backgroundColor: 'rgba(124, 58, 237, 0.05)',
    borderColor: 'rgba(124, 58, 237, 0.2)',
    transform: [{ scale: 1.01 }],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  learningLinkContent: {
    flexDirection: 'row',
    gap: 14,
    alignItems: 'flex-start',
    flex: 1,
  },
  learningLinkImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    flexShrink: 0,
  },
  learningLinkTextContainer: {
    flex: 1,
    minWidth: 0,
    gap: 6,
    paddingRight: 4,
  },
  learningLinkHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
    flexWrap: 'wrap',
    gap: 6,
  },
  articleCategoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    flexShrink: 0,
  },
  articleCategoryText: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  articleReadTime: {
    fontSize: 11,
    fontWeight: '500',
    flexShrink: 0,
  },
  learningLinkExcerpt: {
    fontSize: 12,
    lineHeight: 18,
    marginTop: 4,
    flexShrink: 1,
  },
  learningLinkMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
    flexShrink: 0,
  },
  learningLinkMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  learningLinkMetaText: {
    fontSize: 11,
    fontWeight: '500',
  },
  learningFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    paddingTop: 12,
    paddingHorizontal: 4,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  learningReadTime: {
    fontSize: 12,
    fontWeight: '500',
  },
  learningStack: {
    gap: 12,
  },
  learningCard: {
    borderRadius: 12,
    padding: 14,
    minHeight: 160,
    maxHeight: 240,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
    marginBottom: 12,
  },
  learningCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  learningIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  learningBadge: {
    backgroundColor: palette.cyanLimeGreen,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  learningBadgeText: {
    color: palette.white,
    fontSize: 10,
    fontWeight: '700',
  },
  learningTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
    lineHeight: 20,
  },
  learningDescription: {
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 12,
    opacity: 0.9,
  },

  // Continue Your Colour Journey Card
  continueJourneyCard: {
    marginBottom: Platform.OS === 'web' ? 12 : 16,
    marginHorizontal: 8,
  },
  continueJourneyContent: {
    borderRadius: Platform.OS === 'web' ? 12 : 16,
    padding: Platform.OS === 'web' ? 16 : 20,
    justifyContent: 'space-between',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
  },
  continueJourneyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 8 : 12,
    marginBottom: Platform.OS === 'web' ? 8 : 12,
  },
  continueJourneyText: {
    flex: 1,
  },
  continueJourneyTitle: {
    fontSize: Platform.OS === 'web' ? 16 : 18,
    fontWeight: '700',
  },
  continueJourneyDescription: {
    fontSize: Platform.OS === 'web' ? 12 : 13,
    lineHeight: Platform.OS === 'web' ? 16 : 18,
    marginBottom: Platform.OS === 'web' ? 12 : 16,
    opacity: 0.9,
  },
  continueJourneyActions: {
    marginTop: 'auto',
  },
  continueJourneyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 4,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    minWidth: 140,
    maxWidth: 160,
  },
  continueJourneyButtonText: {
    color: palette.white,
    fontSize: 12,
    fontWeight: '600',
  },
  achievementsContainer: {
    flexDirection: 'row',
    gap: Platform.OS === 'web' ? 12 : 6,
    flexWrap: Platform.OS === 'web' ? 'wrap' : 'nowrap',
    justifyContent: Platform.OS === 'web' ? 'flex-start' : 'space-between',
  },
  achievementsContainerMobile: {
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    gap: 4,
  },
  badgeWrapperMobile: {
    flex: 1,
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '25%',
  },
  badgeWrapper: {
    position: 'relative',
    alignItems: 'center',
    flex: Platform.OS === 'web' ? 0 : 0,
    minWidth: Platform.OS === 'web' ? 80 : 70,
    maxWidth: Platform.OS === 'web' ? 80 : 70,
  },
  achievementBadge: {
    width: Platform.OS === 'web' ? 80 : '100%',
    minHeight: Platform.OS === 'web' ? 100 : 110,
    borderRadius: Platform.OS === 'web' ? 16 : 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Platform.OS === 'web' ? 12 : 14,
    paddingHorizontal: Platform.OS === 'web' ? 8 : 10,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    position: 'relative',
    transition: 'all 0.3s ease',
  },
  badgeContent: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeIcon: {
    width: Platform.OS === 'web' ? 48 : 52,
    height: Platform.OS === 'web' ? 48 : 52,
    borderRadius: Platform.OS === 'web' ? 24 : 26,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 5,
  },
  checkmarkContainer: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    zIndex: 10,
  },
  checkmarkCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: palette.cyanLimeGreen,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: palette.white,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 5,
  },
  badgeLabel: {
    fontSize: Platform.OS === 'web' ? 11 : 12,
    fontWeight: '600',
    textAlign: 'center',
    marginTop: Platform.OS === 'web' ? 8 : 10,
  },

  // Moodboards Section
  colourStoriesSection: {
    marginBottom: Platform.OS === 'web' ? 12 : 16,
    marginHorizontal: 8,
  },
  colourStoriesCard: {
    borderRadius: Platform.OS === 'web' ? 12 : 16,
    padding: Platform.OS === 'web' ? 12 : 16,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
  },
  colourStoriesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Platform.OS === 'web' ? 8 : 12,
  },
  colourStoriesTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 8 : 10,
  },
  colourStoriesTitle: {
    fontSize: Platform.OS === 'web' ? 16 : 18,
    fontWeight: '700',
  },
  seeAllLink: {
    fontSize: Platform.OS === 'web' ? 14 : 15,
    fontWeight: '600',
  },
  communitySection: {
    margin: 8,
    marginTop: 4,
    marginBottom: 12,
  },
  communityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  communityCard: {
    borderRadius: 12,
    padding: 12,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
  },
  moodBoardPreviews: {
    flexDirection: 'row',
    gap: Platform.OS === 'web' ? 4 : 6,
    marginBottom: Platform.OS === 'web' ? 8 : 12,
  },
  moodBoardPreview: {
    flex: 1,
  },
  compactMoodBoard: {
    flex: 1,
  },
  moodBoardImageContainer: {
    aspectRatio: 1,
    borderRadius: Platform.OS === 'web' ? 4 : 8,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    maxHeight: Platform.OS === 'web' ? undefined : 100,
  },
  moodBoardImage: {
    width: '100%',
    height: '100%',
  },
  moodBoardOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: Platform.OS === 'web' ? 3 : 6,
  },
  moodBoardTitle: {
    color: palette.white,
    fontSize: Platform.OS === 'web' ? 9 : 10,
    fontWeight: '600',
    marginBottom: Platform.OS === 'web' ? 1 : 2,
  },
  moodBoardMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  moodBoardAuthor: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 7,
    fontWeight: '500',
  },
  moodBoardLikes: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
  },
  moodBoardLikesText: {
    color: palette.white,
    fontSize: 7,
    fontWeight: '500',
  },
  createButtonContainer: {
    alignItems: 'flex-start',
    marginTop: 0,
  },
  createHueStoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    gap: 3,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    minWidth: 120,
  },
  createHueStoryButtonText: {
    color: palette.white,
    fontSize: 10,
    fontWeight: '600',
  },
  createStoryCard: {
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
    marginBottom: 12,
  },
  createStoryContent: {
    alignItems: 'center',
    gap: 8,
  },
  createStoryTitle: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '700',
  },
  createStorySubtitle: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 14,
    lineHeight: 18,
    textAlign: 'center',
  },
  createColourStoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 6,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    minWidth: 140,
  },
  createColourStoryText: {
    fontSize: 12,
    fontWeight: '600',
  },

  // FAB
  fabContainer: {
    position: 'absolute',
    bottom: 100,
    right: 16,
    zIndex: 1000,
  },
  fab: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },

  // Web Styles
  webContainer: {
    flex: 1,
  },
  webContent: {
    padding: 16,
    paddingTop: 8,
    paddingBottom: 16,
    paddingHorizontal: 24,
    maxWidth: 1400,
    alignSelf: 'center',
    width: '100%',
  },
  webMainLayout: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  webLeftColumn: {
    flex: 2,
    gap: 16,
    marginRight: 12,
  },
  webRightColumn: {
    flex: 1,
    gap: 16,
    flexDirection: 'column',
    marginLeft: 12,
    marginRight: 12,
  },
  adSlotSection: {
    marginTop: 0,
    marginBottom: 0,
    marginHorizontal: 8,
  },
  dummyAd: {
    borderRadius: 12,
    padding: 14,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
    borderWidth: 1,
    borderColor: 'rgba(99, 102, 241, 0.2)',
  },
  adHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  adBadge: {
    backgroundColor: palette.indigoViolet,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
  },
  adBadgeText: {
    color: palette.white,
    fontSize: 9,
    fontWeight: '600',
  },
  adCloseButton: {
    padding: 4,
  },
  adContent: {
    marginBottom: 12,
  },
  adImageContainer: {
    marginBottom: 8,
  },
  adImagePlaceholder: {
    width: '100%',
    height: 100,
    backgroundColor: palette.veryLightGray,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: palette.lightGray,
    borderStyle: 'dashed',
  },
  adTextContent: {
    gap: 6,
  },
  adTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 20,
  },
  adSubtitle: {
    fontSize: 14,
    lineHeight: 18,
    opacity: 0.9,
  },
  adOffer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  adDiscount: {
    fontSize: 12,
    fontWeight: '700',
    color: palette.cyanLimeGreen,
    backgroundColor: palette.lightCyanGreen,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  adTimeLimit: {
    fontSize: 10,
    fontWeight: '500',
  },
  adSocialProof: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 6,
  },
  adSocialText: {
    fontSize: 10,
    fontWeight: '500',
  },
  adCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    gap: 6,
    marginBottom: 8,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  adCTAText: {
    color: palette.white,
    fontSize: 14,
    fontWeight: '700',
  },
  adProgressContainer: {
    gap: 4,
  },
  adProgressTrack: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  adProgressFill: {
    height: '100%',
    borderRadius: 2,
  },
  adProgressText: {
    fontSize: 9,
    fontWeight: '500',
    textAlign: 'center',
  },
  loadingText: {
    fontSize: 16,
    fontWeight: '500',
  },
  primaryButton: {
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default HomeScreen;
